import React, { useState, useEffect } from 'react';

const Subaccounts = ({ userInfo }) => {
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [agencyConnected, setAgencyConnected] = useState(false);
  const [subscriptionRequired, setSubscriptionRequired] = useState(false);
  const [subscriptionError, setSubscriptionError] = useState(null);
  const [showDripModal, setShowDripModal] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [dripSettings, setDripSettings] = useState({
    dripModeEnabled: false,
    dripModeMessagesPerBatch: 20,
    dripModeDelayBetweenMessages: 5000,
    dripModeDelayBetweenBatches: 300000,
  });

  useEffect(() => {
    fetchLocations();
    
    // Check for agency_token success in URL
    const urlParams = new URLSearchParams(window.location.search);
    const agencyTokenStatus = urlParams.get('agency_token');
    
    if (agencyTokenStatus === 'success') {
      setSuccessMessage('✅ Agency access token generated successfully!');
      // Clear the URL parameter
      window.history.replaceState({}, '', window.location.pathname);
      // Auto-hide after 5 seconds
      setTimeout(() => setSuccessMessage(''), 5000);
    } else if (agencyTokenStatus === 'failed') {
      setError('Failed to generate agency token. Please try again.');
      setTimeout(() => setError(''), 5000);
    }
  }, [userInfo]);

  const fetchLocations = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const agencyId = userInfo?.agency?.id;
      
      if (!agencyId) {
        setError('No agency found for user');
        setLoading(false);
        return;
      }
      
      // Fetch locations
      const response = await fetch(`/agencies/${agencyId}/locations`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setLocations(data);
        setError(''); // Clear any previous errors
        setSubscriptionRequired(false);
        setSubscriptionError(null);
      } else if (response.status === 403) {
        // Check if it's a subscription error
        const errorData = await response.json();
        if (errorData.code === 'SUBSCRIPTION_REQUIRED' || 
            errorData.code === 'SUBSCRIPTION_INACTIVE' ||
            errorData.code === 'TRIAL_EXPIRED' ||
            errorData.code === 'SUBSCRIPTION_EXPIRED') {
          setSubscriptionRequired(true);
          setSubscriptionError(errorData);
          setLocations([]);
        } else {
          setError(errorData.message || 'Access denied');
        }
      } else {
        console.error('Failed to fetch locations:', response.status);
        setError('Failed to fetch locations');
        // Set empty array to show the add button even if API fails
        setLocations([]);
      }

      // Check agency connection status
      const agencyResponse = await fetch(`/agencies/${agencyId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (agencyResponse.ok) {
        const agencyData = await agencyResponse.json();
        // Check if agency has valid access token in settings
        // settings is now returned as an object, not a JSON string
        const settings = agencyData.settings || {};
        const hasAgencyToken = !!(settings.agencyAccessToken || settings.ghlCompanyId);
        setAgencyConnected(hasAgencyToken);
      }
    } catch (err) {
      console.error('Network error:', err);
      setError('Network error');
      // Set empty array to show the add button even if API fails
      setLocations([]);
    } finally {
      setLoading(false);
    }
  };

  // Show subscription required screen
  if (subscriptionRequired) {
    return (
      <div className="space-y-6">
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-8 text-center">
          <div className="text-6xl mb-4">💳</div>
          <h2 className="text-2xl font-bold text-yellow-800 mb-2">Subscription Required</h2>
          <p className="text-yellow-700 mb-6">
            {subscriptionError?.message || 'You need an active subscription to access subaccounts.'}
          </p>
          <button
            onClick={() => window.location.href = '/dashboard/billing'}
            className="bg-yellow-600 hover:bg-yellow-700 text-white font-bold py-3 px-6 rounded-lg transition-colors"
          >
            Subscribe Now
          </button>
          <p className="text-sm text-yellow-600 mt-4">
            Start sending WhatsApp messages to your contacts today!
          </p>
        </div>
      </div>
    );
  }

  const connectWhatsApp = async (locationId) => {
    // Redirect directly to WhatsApp connection page without initializing
    // The backend will handle initialization if needed
    window.location.href = `/connect-whatsapp/${locationId}`;
  };

  const generateQRCode = async (locationId) => {
    // Redirect to the qr-latest page which shows the current QR code
    const qrUrl = `${window.location.origin}/whatsapp/qr-latest/${locationId}`;
    window.open(qrUrl, '_blank');
  };

  const disconnectWhatsApp = async (locationId) => {
    if (!window.confirm('Are you sure you want to disconnect WhatsApp? You will need to scan the QR code again to reconnect.')) {
      return;
    }

    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch('/whatsapp/disconnect', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ locationId })
      });

      if (response.ok) {
        alert('WhatsApp disconnected successfully');
        fetchLocations(); // Refresh the location list
      } else {
        alert('Failed to disconnect WhatsApp');
      }
    } catch (err) {
      console.error('Failed to disconnect WhatsApp:', err);
      alert('Network error while disconnecting WhatsApp');
    }
  };

  const deleteLocation = async (locationId) => {
    if (!window.confirm('Are you sure you want to delete this subaccount? This action cannot be undone.')) {
      return;
    }

    try {
      const token = localStorage.getItem('access_token');
      const agencyId = userInfo?.agency?.id;
      
      const response = await fetch(`/agencies/${agencyId}/locations/${locationId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        alert('Subaccount deleted successfully');
        fetchLocations(); // Refresh the location list
      } else {
        alert('Failed to delete subaccount');
      }
    } catch (err) {
      console.error('Failed to delete subaccount:', err);
      alert('Network error while deleting subaccount');
    }
  };

  const connectGHL = () => {
    const agencyId = userInfo?.agency?.id;
    const userId = userInfo?.user?.id;
    if (!agencyId) {
      // If no agency, use the basic OAuth flow
      console.log('No agency found, using basic OAuth flow');
      window.location.href = `/oauth/authorize`;
      return;
    }
    
    // Redirect to OAuth flow with agency context
    console.log('Redirecting to OAuth with agency:', agencyId);
    window.location.href = `/oauth/ghl/install?agencyId=${agencyId}&user=${userId}`;
  };

  const generateAgencyAccessToken = () => {
    // Use backend endpoint to generate proper agency authorization URL
    const agencyId = localStorage.getItem('agencyId');
    const backendUrl = process.env.REACT_APP_API_URL || 'https://admin.gowa.app';
    window.location.href = `${backendUrl}/oauth/ghl/authorize-agency?agencyId=${agencyId}`;
  };

  const openDripModal = async (location) => {
    setSelectedLocation(location);
    // Load current drip settings
    setDripSettings({
      dripModeEnabled: location.dripModeEnabled || false,
      dripModeMessagesPerBatch: location.dripModeMessagesPerBatch || 20,
      dripModeDelayBetweenMessages: location.dripModeDelayBetweenMessages || 5000,
      dripModeDelayBetweenBatches: location.dripModeDelayBetweenBatches || 300000,
    });
    setShowDripModal(true);
  };

  const saveDripSettings = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const agencyId = userInfo?.agency?.id;
      
      const response = await fetch(`/agencies/${agencyId}/locations/${selectedLocation.ghlLocationId}`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(dripSettings)
      });

      if (response.ok) {
        alert('Drip mode settings saved successfully');
        setShowDripModal(false);
        fetchLocations(); // Refresh the location list
      } else {
        alert('Failed to save drip mode settings');
      }
    } catch (err) {
      console.error('Failed to save drip settings:', err);
      alert('Network error while saving drip settings');
    }
  };

  const toggleAutoCreateContacts = async (location) => {
    const newValue = !location.autoCreateContacts;
    
    try {
      const token = localStorage.getItem('access_token');
      const agencyId = userInfo?.agency?.id;
      
      const response = await fetch(`/agencies/${agencyId}/locations/${location.ghlLocationId}`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ autoCreateContacts: newValue })
      });

      if (response.ok) {
        // Update local state
        setLocations(locations.map(loc => 
          loc.ghlLocationId === location.ghlLocationId 
            ? { ...loc, autoCreateContacts: newValue }
            : loc
        ));
        
        // Show feedback
        setSuccessMessage(
          newValue 
            ? '✅ Auto-create contacts enabled - New contacts will be created automatically'
            : '⚠️ Auto-create contacts disabled - Messages from unknown numbers will be ignored'
        );
        setTimeout(() => setSuccessMessage(''), 4000);
      } else {
        alert('Failed to update auto-create contacts setting');
      }
    } catch (err) {
      console.error('Failed to toggle auto-create contacts:', err);
      alert('Network error while updating setting');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-xl text-gray-600">Loading subaccounts...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Success Message */}
      {successMessage && (
        <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg flex items-center justify-between">
          <span className="flex items-center">
            <span className="text-xl mr-2">✅</span>
            {successMessage}
          </span>
          <button
            onClick={() => setSuccessMessage('')}
            className="text-green-600 hover:text-green-800 font-bold"
          >
            ×
          </button>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg flex items-center justify-between">
          <span>{error}</span>
          <button
            onClick={() => setError('')}
            className="text-red-600 hover:text-red-800 font-bold"
          >
            ×
          </button>
        </div>
      )}

      {/* Header */}
      <div className="md:flex md:items-center md:justify-between">
        <div className="flex-1 min-w-0">
          <h2 className="text-2xl font-bold leading-7 text-gray-900 sm:text-3xl sm:truncate">
            Subaccounts
          </h2>
          <p className="mt-1 text-sm text-gray-500">
            Manage your GoHighLevel locations and WhatsApp connections
          </p>
        </div>
        <div className="mt-4 flex md:mt-0 md:ml-4 space-x-3">
          <button
            onClick={() => setShowAddModal(true)}
            className="btn-primary inline-flex items-center"
          >
            <span className="mr-2">➕</span>
            Add Subaccount
          </button>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}

      {/* Locations Grid */}
      {locations.length === 0 ? (
        <div className="text-center py-12">
          <span className="text-6xl">🏢</span>
          <h3 className="mt-2 text-sm font-medium text-gray-900">No subaccounts</h3>
          <p className="mt-1 text-sm text-gray-500">Get started by connecting your first GoHighLevel location.</p>
          <div className="mt-6">
            <button
              onClick={() => setShowAddModal(true)}
              className="btn-primary inline-flex items-center"
            >
              <span className="mr-2">➕</span>
              Add Subaccount
            </button>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {locations.map((location) => (
            <div key={location.ghlLocationId} className="bg-white shadow rounded-lg p-6">
              <div className="flex items-center space-x-3">
                <div className="flex-shrink-0">
                  <span className="text-2xl">🏢</span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">
                    {location.name || 'Unnamed Location'}
                  </p>
                  <p className="text-sm text-gray-500 truncate">
                    ID: {location.ghlLocationId}
                  </p>
                </div>
              </div>
              
              <div className="mt-4 space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-500">WhatsApp Status</span>
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    location.whatsappStatus === 'connected' 
                      ? 'bg-green-100 text-green-800' 
                      : 'bg-gray-100 text-gray-800'
                  }`}>
                    {location.whatsappStatus === 'connected' ? '✅ Connected' : '⏸️ Not Connected'}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-500">Mode</span>
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    location.whatsappSharingMode === 'individual' 
                      ? 'bg-purple-100 text-purple-800' 
                      : 'bg-blue-100 text-blue-800'
                  }`}>
                    {location.whatsappSharingMode === 'individual' ? '👤 Individual' : '👥 Shared'}
                  </span>
                </div>
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-md">
                  <div className="flex items-center">
                    <span className="text-sm font-medium text-gray-700 mr-1">Auto-Create Contacts</span>
                    <span className="text-xs text-gray-500" title="Automatically create new contacts in GHL when receiving messages from unknown numbers">ℹ️</span>
                  </div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleAutoCreateContacts(location);
                    }}
                    className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                      location.autoCreateContacts !== false ? 'bg-green-600' : 'bg-gray-300'
                    }`}
                  >
                    <span
                      className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform ${
                        location.autoCreateContacts !== false ? 'translate-x-5' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>
              </div>

              <div className="mt-6 space-y-3">
                <button
                  onClick={() => window.location.href = `/user-management/${location.ghlLocationId}`}
                  className="btn-primary w-full text-sm"
                >
                  👥 Manage Users
                </button>

                <button
                  onClick={() => openDripModal(location)}
                  className="btn-secondary w-full text-sm"
                >
                  💧 Drip Mode Settings
                </button>

                {location.whatsappStatus !== 'connected' ? (
                  <button
                    onClick={() => connectWhatsApp(location.ghlLocationId)}
                    className="btn-secondary w-full text-sm"
                  >
                    🔗 Connect WhatsApp
                  </button>
                ) : (
                  <button
                    onClick={() => disconnectWhatsApp(location.ghlLocationId)}
                    className="btn-danger w-full text-sm"
                  >
                    🔌 Disconnect WhatsApp
                  </button>
                )}
                
                <button
                  onClick={() => generateQRCode(location.ghlLocationId)}
                  className="btn-secondary w-full text-sm"
                >
                  📱 Generate QR Code
                </button>

                <button
                  onClick={() => deleteLocation(location.ghlLocationId)}
                  className="btn-danger w-full text-sm"
                >
                  🗑️ Delete Subaccount
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add Location Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3 text-center">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Add New Subaccount</h3>
              <div className="mt-2 px-7 py-3">
                <div className="bg-blue-50 border border-blue-200 rounded-md p-4 mb-4">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <span className="text-blue-400">ℹ️</span>
                    </div>
                    <div className="ml-3">
                      <h3 className="text-sm font-medium text-blue-800">
                        How it works:
                      </h3>
                      <div className="mt-2 text-sm text-blue-700">
                        <ul className="list-decimal pl-5 space-y-1">
                          <li>Click "Connect GoHighLevel Subaccount" below.</li>
                          <li>Select "GoWA" as your conversation provider and proceed.</li>
                          <li>A custom menu link containing a QR code will be added to the subaccount.</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="items-center px-4 py-3">
                <button
                  onClick={() => {
                    setShowAddModal(false);
                    connectGHL();
                  }}
                  className="btn-primary w-full"
                >
                  Connect GoHighLevel Subaccount
                </button>
                <button
                  onClick={() => setShowAddModal(false)}
                  className="btn-secondary w-full mt-3"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Drip Mode Settings Modal */}
      {showDripModal && selectedLocation && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-5 border w-full max-w-2xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                💧 Drip Mode Settings - {selectedLocation.name}
              </h3>
              <div className="mt-2 px-7 py-3 space-y-4">
                <div className="bg-blue-50 border border-blue-200 rounded-md p-4 mb-4">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <span className="text-blue-400">ℹ️</span>
                    </div>
                    <div className="ml-3">
                      <h3 className="text-sm font-medium text-blue-800">
                        What is Drip Mode?
                      </h3>
                      <div className="mt-2 text-sm text-blue-700">
                        <p>Drip mode helps prevent WhatsApp bans by adding delays between messages. Instead of sending all messages instantly, they are queued and sent with controlled delays.</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Enable/Disable Toggle */}
                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-md">
                  <div>
                    <label className="text-sm font-medium text-gray-900">Enable Drip Mode</label>
                    <p className="text-xs text-gray-500">Queue messages with delays to prevent bans</p>
                  </div>
                  <button
                    onClick={() => setDripSettings({...dripSettings, dripModeEnabled: !dripSettings.dripModeEnabled})}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      dripSettings.dripModeEnabled ? 'bg-green-600' : 'bg-gray-300'
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        dripSettings.dripModeEnabled ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>

                {/* Messages Per Batch */}
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-900">
                    Messages Per Batch
                  </label>
                  <input
                    type="number"
                    value={dripSettings.dripModeMessagesPerBatch}
                    onChange={(e) => setDripSettings({...dripSettings, dripModeMessagesPerBatch: parseInt(e.target.value)})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    min="1"
                    max="100"
                  />
                  <p className="text-xs text-gray-500">Number of messages to send before taking a longer break</p>
                </div>

                {/* Delay Between Messages */}
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-900">
                    Delay Between Messages (milliseconds)
                  </label>
                  <input
                    type="number"
                    value={dripSettings.dripModeDelayBetweenMessages}
                    onChange={(e) => setDripSettings({...dripSettings, dripModeDelayBetweenMessages: parseInt(e.target.value)})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    min="1000"
                    max="60000"
                    step="1000"
                  />
                  <p className="text-xs text-gray-500">
                    Delay between each message ({(dripSettings.dripModeDelayBetweenMessages / 1000).toFixed(1)} seconds)
                  </p>
                </div>

                {/* Delay Between Batches */}
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-900">
                    Delay Between Batches (milliseconds)
                  </label>
                  <input
                    type="number"
                    value={dripSettings.dripModeDelayBetweenBatches}
                    onChange={(e) => setDripSettings({...dripSettings, dripModeDelayBetweenBatches: parseInt(e.target.value)})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    min="60000"
                    max="3600000"
                    step="60000"
                  />
                  <p className="text-xs text-gray-500">
                    Longer break after each batch ({(dripSettings.dripModeDelayBetweenBatches / 60000).toFixed(1)} minutes)
                  </p>
                </div>

                {/* Summary */}
                {dripSettings.dripModeEnabled && (
                  <div className="bg-green-50 border border-green-200 rounded-md p-4">
                    <h4 className="text-sm font-medium text-green-800 mb-2">Summary</h4>
                    <ul className="text-sm text-green-700 space-y-1">
                      <li>• Send {dripSettings.dripModeMessagesPerBatch} messages per batch</li>
                      <li>• Wait {(dripSettings.dripModeDelayBetweenMessages / 1000).toFixed(1)} seconds between each message</li>
                      <li>• Wait {(dripSettings.dripModeDelayBetweenBatches / 60000).toFixed(1)} minutes between batches</li>
                      <li>• Example: 100 messages = ~{Math.ceil(100 / dripSettings.dripModeMessagesPerBatch)} batches over ~{((100 * dripSettings.dripModeDelayBetweenMessages + Math.ceil(100 / dripSettings.dripModeMessagesPerBatch) * dripSettings.dripModeDelayBetweenBatches) / 60000).toFixed(1)} minutes</li>
                    </ul>
                  </div>
                )}
              </div>
              <div className="items-center px-4 py-3 flex space-x-3">
                <button
                  onClick={saveDripSettings}
                  className="btn-primary flex-1"
                >
                  Save Settings
                </button>
                <button
                  onClick={() => setShowDripModal(false)}
                  className="btn-secondary flex-1"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Subaccounts;